import projectsData from '../data/projects.json';
import { debugLog } from './debug';
import { normalizeSocialLink } from './linkUtils';

// Helper function to load about data dynamically (static content only)
const loadAboutData = async () => {
  try {
    const response = await fetch(`/data/about.json?v=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch about data: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error loading about data:', error);
    return {
      imprint: {},
      inquiries: {},
      newsletter: {},
      annotations: [],
      intro: '',
      bio: null,
      backgroundImage: null,
      themeColor: '#091115'
    };
  }
};

// Helper function to load events dynamically
const loadEventsData = async () => {
  try {
    const response = await fetch(`/data/events.json?v=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch events data: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error loading events data:', error);
    return [];
  }
};

// Helper function to load press mentions dynamically
const loadPressData = async () => {
  try {
    const response = await fetch(`/data/press.json?v=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch press data: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error loading press data:', error);
    return [];
  }
};

// Helper function to load collaborators dynamically
const loadCollaboratorsData = async () => {
  try {
    const response = await fetch(`/data/collaborators.json?v=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch collaborators data: ${response.status}`);
    }
    const data = await response.json();
    // Handle the case where collaborators.json has a _comment wrapper
    const collaborators = data.collaborators || data;
    // Create a map for quick lookup by ID
    const collaboratorsMap = new Map();
    collaborators.forEach(collab => {
      collaboratorsMap.set(collab.id, {
        ...collab,
        link: normalizeSocialLink(collab.link)
      });
    });
    return collaboratorsMap;
  } catch (error) {
    console.error('Error loading collaborators data:', error);
    return new Map();
  }
};

// Helper function to merge collaborator details into events
const mergeCollaboratorsIntoEvents = (events, collaboratorsMap) => {
  return events.map(event => {
    if (!event.collaborators || !Array.isArray(event.collaborators)) {
      return event;
    }

    const enrichedCollaborators = event.collaborators.map(collab => {
      const collaboratorDetails = collaboratorsMap.get(collab.collaborator_id);
      if (collaboratorDetails) {
        return {
          ...collab,
          name: collaboratorDetails.name,
          bio: collaboratorDetails.bio,
          link: normalizeSocialLink(collaboratorDetails.link),
          profession: collaboratorDetails.profession || []
        };
      }
      // If collaborator not found, return as-is (just collaborator_id and role)
      return collab;
    });

    return {
      ...event,
      collaborators: enrichedCollaborators
    };
  });
};


export const loadProjects = async () => {
  // Load data from separate files
  const [aboutData, eventsData, pressData, collaboratorsMap] = await Promise.all([
    loadAboutData(),
    loadEventsData(),
    loadPressData(),
    loadCollaboratorsData()
  ]);

  // Merge collaborator details into events
  const enrichedEventsData = mergeCollaboratorsIntoEvents(eventsData, collaboratorsMap);

  const projects = await Promise.all(
    projectsData.map(async (project) => {
      try {
        // Special case: about
        if (project.id === 'about') {
          return {
            ...project,
            // About page metadata
            backgroundImage: aboutData.backgroundImage || null,
            themeColor: aboutData.themeColor || project.themeColor,
            imprint: aboutData.imprint || {},
            inquiries: aboutData.inquiries || {},
            newsletter: aboutData.newsletter || {},
            annotations: aboutData.annotations || [],
            // Content data
            intro: aboutData.intro || '',
            bio: aboutData.bio || null,
            // Dynamic data from separate files
            events: enrichedEventsData,
            press_mentions: pressData
          };
        }

        // Handle content pages (terms, privacy, shipping, legal-notice)
        if (['terms', 'privacy', 'shipping', 'legal-notice'].includes(project.id)) {
          try {
            const module = await import(`../data/${project.id}.json`);
            return { ...project, ...module.default };
          } catch (error) {
            // Return basic project data if JSON doesn't exist
            return project;
          }
        }

        // Regular case: only load data for Public projects
        if (project.status === 'Public') {
          try {
            debugLog(`🔄 Loading full data for project: ${project.id}`);
            const module = await import(`../data/${project.id}.json`);
            debugLog(`✅ Successfully loaded data for ${project.id}:`, module.default ? 'has data' : 'no data');
            return { ...project, ...module.default };
          } catch (error) {
            console.error(`❌ Failed to load data for ${project.id}:`, error);
            return project;
          }
        }
        return project;
      } catch (error) {
        console.warn(`Failed to load project ${project.id}:`, error);
        return project;
      }
    })
  );
  return projects;
};
